import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { KTIcon } from '../../../../_metronic/helpers';
import { KTCard, KTCardBody } from '../../../../_metronic/helpers';
import { PageTitle } from '../../../../_metronic/layout/core';
import { toast } from 'react-toastify';
import { get_all_assets } from '../../digital-assets/api/assetsApi';
import { Asset } from '../../digital-assets/types/assetTypes';
import { add_project } from '../api/projectsApi';
import { permissionCheck } from '../../../helper-functions/PermissionCheck';
import axios from 'axios';
import { config_multipart } from '../../api-helper/api-header-configs';

interface LinkedAsset {
  asset: Asset;
  licenseFile?: File;
  licenseFileName?: string;
}


const CreateProjectPage: React.FC = () => {
  const navigate = useNavigate();
  const canCreateProject = Boolean(permissionCheck('project-create'));
  const baseURL = process.env.REACT_APP_API_URL;

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    status: 'active' as 'active' | 'inactive',
  });

  const [subprojects, setSubprojects] = useState<Array<{ id: number; subproject_name: string; description: string; status: string }>>([]);
  const [newSubproject, setNewSubproject] = useState({
    subproject_name: '',
    description: '',
    status: 'active' as 'active' | 'inactive'
  });
  const [showSubprojectForm, setShowSubprojectForm] = useState(false);

  const [assets, setAssets] = useState<Asset[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingAssets, setIsLoadingAssets] = useState(false);
  const [assetSearchTerm, setAssetSearchTerm] = useState('');

  // Asset linking states
  const [projectId, setProjectId] = useState<number | null>(null);
  const [isLinkingAssets, setIsLinkingAssets] = useState(false);
  const [activeTab, setActiveTab] = useState<'create' | 'link'>('create');
  const [currentAssetSelection, setCurrentAssetSelection] = useState<Asset | null>(null);
  const [currentLicenseFile, setCurrentLicenseFile] = useState<File | null>(null);
  const [linkedAssets, setLinkedAssets] = useState<LinkedAsset[]>([]);
  const [isLinkingAsset, setIsLinkingAsset] = useState(false);

  // Redirect if no permission
  useEffect(() => {
    if (!canCreateProject) {
      toast.error('You do not have permission to create projects');
      navigate('/projects/list');
    }
  }, [canCreateProject, navigate]);

  // Load assets on mount
  useEffect(() => {
    if (isLinkingAssets) {
      loadAssets();
    }
  }, [isLinkingAssets]);

  const loadAssets = async () => {
    try {
      setIsLoadingAssets(true);
      const response = await get_all_assets();
      const assetsList = response?.assets || [];
      setAssets(assetsList);
    } catch (error) {
      console.error('Error loading assets:', error);
      toast.error('Failed to load assets');
    } finally {
      setIsLoadingAssets(false);
    }
  };

  const handleAddSubproject = () => {
    if (!newSubproject.subproject_name.trim()) {
      toast.error('Please enter subproject name');
      return;
    }
    const subprojectToAdd = {
      id: Date.now(),
      subproject_name: newSubproject.subproject_name.trim(),
      description: newSubproject.description.trim(),
      status: newSubproject.status,
    };
    setSubprojects([...subprojects, subprojectToAdd]);
    setNewSubproject({
      subproject_name: '',
      description: '',
      status: 'active'
    });
    setShowSubprojectForm(false);
    toast.success('Subproject added successfully');
  };

  const handleRemoveSubproject = (id: number) => {
    setSubprojects(subprojects.filter(sp => sp.id !== id));
  };


  // Save project without assets
  const handleSaveProject = async () => {
    if (!formData.name.trim()) {
      toast.error('Please enter project name');
      return;
    }

    try {
      setIsLoading(true);

      const payload = {
        project_name: formData.name,
        description: formData.description || '',
        status: formData.status === 'active' ? '1' : '0',
        subprojects: subprojects.length > 0 ? subprojects.map(sp => ({ 
          subproject_name: sp.subproject_name,
          description: sp.description,
          status: sp.status === 'active' ? '1' : '0'
        })) : undefined,
      };

      const response = await add_project(payload);
      
      // Check different response structures
      // Response structure: { status: true, data: { project: { id: 30 } } }
      const projectId = response?.data?.project?.id || response?.data?.id || response?.id;
      const isSuccess = response?.status === true || response?.status === 'success' || projectId;
      
      if (isSuccess) {
        toast.success('Project saved successfully');
        navigate('/projects/list');
      } else {
        console.log('Response structure:', response);
        toast.error(response?.message || 'Failed to save project');
      }
    } catch (error: any) {
      console.error('Error saving project:', error);
      toast.error(error?.response?.data?.message || 'Failed to save project');
    } finally {
      setIsLoading(false);
    }
  };

  // Save project and start linking assets
  const handleSaveProjectAndLinkAssets = async () => {
    if (!formData.name.trim()) {
      toast.error('Please enter project name');
      return;
    }

    try {
      setIsLoading(true);

      const payload = {
        project_name: formData.name,
        description: formData.description || '',
        status: formData.status === 'active' ? '1' : '0',
        subprojects: subprojects.length > 0 ? subprojects.map(sp => ({ 
          subproject_name: sp.subproject_name,
          description: sp.description,
          status: sp.status === 'active' ? '1' : '0'
        })) : undefined,
      };

      const response = await add_project(payload);
      
      // add_project returns response.data, so response is already the data object
      // Check different possible response structures
      console.log('Save Project & Link Assets - Full Response:', response);
      
      // Try multiple paths for project ID
      // Response structure: { status: true, data: { project: { id: 30 } } }
      const projectId = response?.data?.project?.id || response?.data?.id || response?.id || response?.project_id;
      const isSuccess = response?.status === true || 
                       response?.status === 'success' || 
                       projectId || 
                       (response && typeof response === 'object');
      
      console.log('Extracted projectId:', projectId);
      console.log('isSuccess:', isSuccess);
      
      // If we got a response (even without explicit status), assume success
      // This handles cases where backend returns just the project object
      if (isSuccess) {
        // Set projectId if found
        if (projectId) {
          setProjectId(Number(projectId));
          setIsLinkingAssets(true);
          setActiveTab('link');
          toast.success('Project saved successfully. Now link assets.');
          await loadAssets();
        } else {
          console.warn('Project ID not found in response. Will need to handle when linking assets.');
          console.log('Full response structure:', JSON.stringify(response, null, 2));
          toast.error('Project saved but ID not found. Please refresh and try again.');
        }
      } else {
        console.error('Failed to save project. Response:', response);
        toast.error(response?.message || 'Failed to save project');
      }
    } catch (error: any) {
      console.error('Error saving project:', error);
      toast.error(error?.response?.data?.message || 'Failed to save project');
    } finally {
      setIsLoading(false);
    }
  };

  // Link single asset with license file
  const handleLinkAsset = async () => {
    if (!currentAssetSelection) {
      toast.error('Please select an asset');
      return;
    }

    if (!projectId) {
      toast.error('Project ID not found');
      return;
    }

    try {
      setIsLinkingAsset(true);

      const linkFormData = new FormData();
      linkFormData.append('project_id', String(projectId));
      linkFormData.append('asset_id', String(currentAssetSelection.id || ''));
      
      if (currentLicenseFile) {
        linkFormData.append('license_file', currentLicenseFile);
      }

      // Link asset to project API call
      const response = await axios.post(
        `${baseURL}/projects/${projectId}/assets`,
        linkFormData,
        config_multipart
      );

      if (response.data?.status || response.data) {
        // Add to linked assets list
        setLinkedAssets(prev => [...prev, {
          asset: currentAssetSelection,
          licenseFile: currentLicenseFile || undefined,
          licenseFileName: currentLicenseFile?.name,
        }]);

        // Reset current selection automatically to allow linking another asset
        setCurrentAssetSelection(null);
        setCurrentLicenseFile(null);
        // Keep search term so user can continue searching
        
        toast.success('Asset linked successfully. You can link another asset or click Finish.');
      } else {
        toast.error('Failed to link asset');
      }
    } catch (error: any) {
      console.error('Error linking asset:', error);
      toast.error(error?.response?.data?.message || 'Failed to link asset');
    } finally {
      setIsLinkingAsset(false);
    }
  };

  // Add another asset (reset current selection)
  const handleAddAnotherAsset = () => {
    setCurrentAssetSelection(null);
    setCurrentLicenseFile(null);
    setAssetSearchTerm('');
  };

  // Finish linking assets
  const handleFinish = () => {
    navigate('/projects/list');
  };

  const filteredAssets = assets.filter(asset =>
    asset.name.toLowerCase().includes(assetSearchTerm.toLowerCase()) ||
    asset.description?.toLowerCase().includes(assetSearchTerm.toLowerCase())
  );

  // Filter out already linked assets
  const availableAssets = filteredAssets.filter(asset => 
    !linkedAssets.some(la => la.asset.id === asset.id)
  );

  if (!canCreateProject) {
    return null;
  }

  return (
    <>
      <PageTitle>Create New Project</PageTitle>
      <div className='d-flex flex-column flex-column-fluid'>
        {/* Header */}
        <div className='toolbar' id='kt_toolbar'>
          <div id='kt_toolbar_container' className='container-fluid d-flex flex-stack'>
            <div className='d-flex align-items-center gap-2'>
              <button
                type='button'
                className='btn btn-sm btn-light d-flex align-items-center'
                onClick={() => navigate('/projects/list')}
                disabled={isLoading}
                style={{ cursor: isLoading ? 'not-allowed' : 'pointer' }}
              >
                <KTIcon iconName='arrow-left' className='fs-2 me-2' />
                <span>Back to Projects</span>
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div id='kt_content' className='content flex-column-fluid'>
          <div className='container-xxl'>
            {/* Tabs Navigation */}
            <div className='card card-custom mb-5'>
              <div className='card-header card-header-stretch overflow-auto'>
                <ul
                  className='nav nav-stretch nav-line-tabs fw-bold border-transparent flex-nowrap'
                  role='tablist'
                >
                  <li className='nav-item'>
                    <a
                      className={`nav-link cursor-pointer ${activeTab === 'create' ? 'active' : ''}`}
                      onClick={() => setActiveTab('create')}
                      role='tab'
                    >
                      Create New Project
                    </a>
                  </li>
                  {/* Asset Link Tab - Commented out as per requirements */}
                  {/* <li className='nav-item'>
                    <a
                      className={`nav-link cursor-pointer ${activeTab === 'link' ? 'active' : ''} ${!isLinkingAssets ? 'disabled' : ''}`}
                      onClick={() => {
                        if (isLinkingAssets) {
                          setActiveTab('link');
                        } else {
                          toast.warning('Please save the project first to link assets');
                        }
                      }}
                      role='tab'
                      style={{ opacity: !isLinkingAssets ? 0.5 : 1, cursor: !isLinkingAssets ? 'not-allowed' : 'pointer' }}
                    >
                      Link Assets to Project
                    </a>
                  </li> */}
                </ul>
              </div>
            </div>

            {/* Tab Content */}
            {activeTab === 'create' ? (
              // Project Creation Tab
              <form onSubmit={(e) => e.preventDefault()}>
              {/* Full Width Project Form */}
              <div className='row g-5 g-xl-8'>
                <div className='col-xl-12'>
                  {/* Project Information */}
                  <KTCard>
                    <KTCardBody>
                      <h3 className='mb-5'>Project Information</h3>
                      
                      <div className='row'>
                        <div className='col-md-6 mb-5'>
                          <label className='form-label fw-bold required'>Project Name</label>
                          <input
                            type='text'
                            className='form-control'
                            placeholder='Enter project name'
                            value={formData.name}
                            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                            required
                            disabled={isLoading}
                          />
                        </div>

                        <div className='col-md-6 mb-5'>
                          <label className='form-label fw-bold required'>Status</label>
                          <select
                            className='form-select'
                            value={formData.status}
                            onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value as any }))}
                            required
                            disabled={isLoading}
                          >
                            <option value='active'>Active</option>
                            <option value='inactive'>Inactive</option>
                          </select>
                        </div>
                      </div>

                      <div className='mb-5'>
                        <label className='form-label fw-bold'>Description</label>
                        <textarea
                          className='form-control'
                          rows={4}
                          placeholder='Enter project description'
                          value={formData.description}
                          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                          disabled={isLoading}
                        ></textarea>
                      </div>
                    </KTCardBody>
                  </KTCard>

                  {/* Subprojects Section */}
                  <KTCard className='mt-5'>
                    <KTCardBody>
                      <div className='d-flex justify-content-between align-items-center mb-5'>
                        <h3 className='mb-0'>Subprojects</h3>
                        {!showSubprojectForm && (
                          <button
                            type='button'
                            className='btn btn-primary'
                            onClick={() => setShowSubprojectForm(true)}
                            disabled={isLoading}
                          >
                            <KTIcon iconName='plus' className='fs-2' />
                            Add Subproject
                          </button>
                        )}
                      </div>
                      
                      {showSubprojectForm && (
                        <div className='card border mb-5'>
                          <div className='card-body'>
                            <div className='d-flex justify-content-between align-items-center mb-4'>
                              <h5 className='mb-0'>Add New Subproject</h5>
                              <button
                                type='button'
                                className='btn btn-sm btn-icon btn-light'
                                onClick={() => {
                                  setShowSubprojectForm(false);
                                  setNewSubproject({
                                    subproject_name: '',
                                    description: '',
                                    status: 'active'
                                  });
                                }}
                              >
                                <KTIcon iconName='cross' className='fs-2' />
                              </button>
                            </div>
                            <div className='row g-3'>
                              <div className='col-md-12'>
                                <label className='form-label fw-bold required'>Subproject Name</label>
                                <input
                                  type='text'
                                  className='form-control'
                                  placeholder='Enter subproject name'
                                  value={newSubproject.subproject_name}
                                  onChange={(e) => setNewSubproject(prev => ({ ...prev, subproject_name: e.target.value }))}
                                  disabled={isLoading}
                                />
                              </div>
                              <div className='col-md-12'>
                                <label className='form-label fw-bold'>Description</label>
                                <textarea
                                  className='form-control'
                                  rows={3}
                                  placeholder='Enter subproject description'
                                  value={newSubproject.description}
                                  onChange={(e) => setNewSubproject(prev => ({ ...prev, description: e.target.value }))}
                                  disabled={isLoading}
                                ></textarea>
                              </div>
                              <div className='col-md-12'>
                                <label className='form-label fw-bold required'>Status</label>
                                <select
                                  className='form-select'
                                  value={newSubproject.status}
                                  onChange={(e) => setNewSubproject(prev => ({ ...prev, status: e.target.value as 'active' | 'inactive' }))}
                                  disabled={isLoading}
                                >
                                  <option value='active'>Active</option>
                                  <option value='inactive'>Inactive</option>
                                </select>
                              </div>
                              <div className='col-md-12'>
                                <div className='d-flex gap-2'>
                                  <button
                                    type='button'
                                    className='btn btn-primary'
                                    onClick={handleAddSubproject}
                                    disabled={isLoading || !newSubproject.subproject_name.trim()}
                                  >
                                    <KTIcon iconName='check' className='fs-2' />
                                    Add Subproject
                                  </button>
                                  <button
                                    type='button'
                                    className='btn btn-light'
                                    onClick={() => {
                                      setShowSubprojectForm(false);
                                      setNewSubproject({
                                        subproject_name: '',
                                        description: '',
                                        status: 'active'
                                      });
                                    }}
                                    disabled={isLoading}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {subprojects.length > 0 && (
                        <div className='border rounded p-4' style={{ maxHeight: '300px', overflowY: 'auto' }}>
                          {subprojects.map((subproject, index) => (
                            <div key={subproject.id} className='border-bottom pb-3 mb-3'>
                              <div className='d-flex justify-content-between align-items-start'>
                                <div className='flex-grow-1'>
                                  <div className='fw-bold fs-5 mb-1'>{subproject.subproject_name}</div>
                                  {subproject.description && (
                                    <div className='text-muted fs-6 mb-2'>{subproject.description}</div>
                                  )}
                                  <span className={`badge badge-light-${subproject.status === 'active' ? 'success' : 'danger'}`}>
                                    {subproject.status === 'active' ? 'Active' : 'Inactive'}
                                  </span>
                                </div>
                                <button
                                  type='button'
                                  className='btn btn-sm btn-icon btn-light-danger'
                                  onClick={() => handleRemoveSubproject(subproject.id)}
                                  disabled={isLoading}
                                >
                                  <KTIcon iconName='trash' className='fs-2' />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      {subprojects.length === 0 && !showSubprojectForm && (
                        <div className='text-muted text-center py-3'>No subprojects added yet</div>
                      )}
                    </KTCardBody>
                  </KTCard>
                </div>
              </div>

              {/* Project Creation Info Card - Bottom */}
              <div className='row g-5 g-xl-8 mt-3'>
                <div className='col-xl-12'>
                  <KTCard>
                    <KTCardBody>
                      <h3 className='mb-5'>Project Creation</h3>
                      <div className='alert alert-info'>
                        <KTIcon iconName='information-5' className='fs-2x me-3' />
                        <div>
                          <strong>Save Options:</strong>
                          <ul className='mt-3 mb-0'>
                            <li><strong>Save Project:</strong> Save project information with subprojects</li>
                          </ul>
                        </div>
                      </div>
                    </KTCardBody>
                  </KTCard>
                </div>
              </div>

            </form>
            ) : (
              // Asset Linking Tab
              <div className='row g-5 g-xl-8'>
                {/* Left Column - Linked Assets */}
                <div className='col-xl-6'>
                  <KTCard>
                    <KTCardBody>
                      <h3 className='mb-5'>Linked Assets</h3>
                      {linkedAssets.length === 0 ? (
                        <div className='text-muted text-center py-5'>
                          No assets linked yet. Select an asset and link it.
                        </div>
                      ) : (
                        <>
                          <div className='border rounded p-4' style={{ maxHeight: '400px', overflowY: 'auto' }}>
                            {linkedAssets.map((linkedAsset, index) => (
                              <div key={index} className='border-bottom pb-3 mb-3'>
                                <div className='d-flex align-items-start justify-content-between'>
                                  <div className='flex-grow-1'>
                                    <div className='fw-bold mb-2'>{linkedAsset.asset.name}</div>
                                    {linkedAsset.asset.description && (
                                      <div className='text-muted fs-7 mb-2'>{linkedAsset.asset.description}</div>
                                    )}
                                    {linkedAsset.licenseFileName && (
                                      <div className='text-success fs-7'>
                                        <KTIcon iconName='check' className='fs-6' />
                                        License: {linkedAsset.licenseFileName}
                                      </div>
                                    )}
                                  </div>
                                  <span className='badge badge-light-success'>Linked</span>
                                </div>
                              </div>
                            ))}
                          </div>
                          <div className='mt-4'>
                            <div className='alert alert-info'>
                              <strong>{linkedAssets.length}</strong> asset(s) linked to this project
                            </div>
                          </div>
                        </>
                      )}
                    </KTCardBody>
                  </KTCard>
                </div>

                {/* Right Column - Select Asset */}
                <div className='col-xl-6'>
                  <KTCard>
                    <KTCardBody>
                      <h3 className='mb-5'>Select Asset</h3>
                      {isLoadingAssets ? (
                        <div className='text-center py-5'>
                          <span className='spinner-border spinner-border-sm'></span>
                          <span className='ms-2'>Loading assets...</span>
                        </div>
                      ) : (
                        <>
                          <div className='mb-5'>
                            <input
                              type='text'
                              className='form-control'
                              placeholder='Search assets...'
                              value={assetSearchTerm}
                              onChange={(e) => setAssetSearchTerm(e.target.value)}
                              disabled={isLinkingAsset}
                            />
                          </div>
                          <div className='border rounded p-4' style={{ maxHeight: '400px', overflowY: 'auto' }}>
                            {availableAssets.length === 0 ? (
                              <div className='text-muted text-center py-5'>No assets available</div>
                            ) : (
                              availableAssets.map((asset) => (
                                <div
                                  key={asset.id}
                                  className={`border rounded p-3 mb-3 cursor-pointer ${currentAssetSelection?.id === asset.id ? 'border-primary bg-light-primary' : ''}`}
                                  onClick={() => setCurrentAssetSelection(asset)}
                                  style={{ cursor: 'pointer' }}
                                >
                                  <div className='d-flex align-items-start'>
                                    <div className='form-check me-3 mt-1'>
                                      <input
                                        className='form-check-input'
                                        type='radio'
                                        name='assetSelection'
                                        checked={currentAssetSelection?.id === asset.id}
                                        onChange={() => setCurrentAssetSelection(asset)}
                                        disabled={isLinkingAsset}
                                      />
                                    </div>
                                    <div className='flex-grow-1'>
                                      <div className='fw-bold mb-2 fs-5'>{asset.name}</div>
                                      {asset.description && (
                                        <div className='text-muted fs-6 mb-2'>{asset.description}</div>
                                      )}
                                      <div className='d-flex gap-2'>
                                        <span className='badge badge-light-primary'>{asset.category}</span>
                                        {asset.tags.map((tag, idx) => (
                                          <span key={idx} className='badge badge-light-info'>{tag}</span>
                                        ))}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))
                            )}
                          </div>
                        </>
                      )}
                    </KTCardBody>
                  </KTCard>

                  {/* License File Upload */}
                  {currentAssetSelection && (
                    <KTCard className='mt-5'>
                      <KTCardBody>
                        <h3 className='mb-5'>Upload License File (Optional)</h3>
                        <div className='mb-3'>
                          <label className='form-label fw-bold'>Selected Asset: {currentAssetSelection.name}</label>
                        </div>
                        <div className='mb-3'>
                          <input
                            type='file'
                            className='form-control'
                            accept='.pdf,.doc,.docx,.txt'
                            onChange={(e) => {
                              const file = e.target.files?.[0] || null;
                              setCurrentLicenseFile(file);
                            }}
                            disabled={isLinkingAsset}
                          />
                          {currentLicenseFile && (
                            <div className='text-success fs-7 mt-2'>
                              <KTIcon iconName='check' className='fs-6' />
                              {currentLicenseFile.name}
                            </div>
                          )}
                        </div>
                      </KTCardBody>
                    </KTCard>
                  )}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            {activeTab === 'create' ? (
              <div className='d-flex justify-content-end gap-3 mt-5 mb-5'>
                <button
                  type='button'
                  className='btn btn-light'
                  onClick={() => navigate('/projects/list')}
                  disabled={isLoading}
                >
                  Cancel
                </button>
                <button
                  type='button'
                  className='btn btn-primary'
                  onClick={handleSaveProject}
                  disabled={isLoading || !formData.name.trim()}
                >
                  {isLoading ? (
                    <>
                      <span className='spinner-border spinner-border-sm me-2'></span>
                      Saving...
                    </>
                  ) : (
                    <>
                      <KTIcon iconName='check' className='fs-2' />
                      Save Project
                    </>
                  )}
                </button>
                {/* Asset Link Button - Removed as per requirements */}
                {/* <button
                  type='button'
                  className='btn btn-success'
                  onClick={handleSaveProjectAndLinkAssets}
                  disabled={isLoading || !formData.name.trim()}
                >
                  {isLoading ? (
                    <>
                      <span className='spinner-border spinner-border-sm me-2'></span>
                      Saving...
                    </>
                  ) : (
                    <>
                      <KTIcon iconName='link' className='fs-2' />
                      Save Project & Link Assets
                    </>
                  )}
                </button> */}
              </div>
            ) : (
              <div className='d-flex justify-content-end gap-3 mt-5 mb-5'>
                <button
                  type='button'
                  className='btn btn-primary'
                  onClick={handleLinkAsset}
                  disabled={isLinkingAsset || !currentAssetSelection}
                >
                  {isLinkingAsset ? (
                    <>
                      <span className='spinner-border spinner-border-sm me-2'></span>
                      Linking...
                    </>
                  ) : (
                    <>
                      <KTIcon iconName='link' className='fs-2 me-2' />
                      Link Asset
                    </>
                  )}
                </button>
                <button
                  type='button'
                  className='btn btn-success'
                  onClick={handleFinish}
                  disabled={isLinkingAsset}
                >
                  <KTIcon iconName='check' className='fs-2 me-2' />
                  Finish
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default CreateProjectPage;
